/**
 * For handling reddit settings that use the radio button element.
 *
 * These look like
 *
 * <div data-testid="content-and-activity-option" role="button" aria-label="Show all">
 *  <div>
 *    <span>Show all</span>
 *    <span>Show all posts, comments, and communities you're active in on your profile</span>
 *  </div>
 *  <faceplate-radio-input value="show_all" checked aria-checked="true" role="radio" aria-disabled="false">
 *  </faceplate-radio-input>
 * </div>
 *
 * and are nested in shadowRoots like the other elements.
 */

import { SpanStatusCode } from '@opentelemetry/api'

import { withSpan } from '$extensionSrc/utils/tracing'

import { getWaitForElementToAppearPromise } from '../interruptiblePromises'

export function getLabelFromRadioEl(element: HTMLElement): string | null {
  return element.ariaLabel
}

export function getValueFromRadioEl(element: HTMLElement): boolean | undefined {
  const radioButton = element.querySelector(`faceplate-radio-input`)
  if (!radioButton) {
    return undefined
  }
  return radioButton.ariaChecked === 'true'
}

export async function getNestedShadowrootSection(
  sections: string[],
): Promise<HTMLElement | undefined> {
  if (sections.length === 0) {
    return Promise.resolve(undefined)
  }

  return getWaitForElementToAppearPromise<HTMLElement>(
    document,
    () => {
      let curSectionElement = document.querySelector(sections[0])
      for (const section of sections.slice(1)) {
        if (!curSectionElement) {
          curSectionElement = undefined
          break
        }

        curSectionElement = curSectionElement.shadowRoot.querySelector(section)
      }

      return curSectionElement as HTMLElement | undefined
    },
    5000,
  )
}

export async function getRadioSelectedEl(
  sections: string[],
  dataTestid: string,
): Promise<HTMLElement | undefined> {
  return withSpan(
    'reddit.radioUtils.getRadioSelectedEl',
    async (span) => {
      const section = await getNestedShadowrootSection(sections)

      await getWaitForElementToAppearPromise(
        section,
        () => section.shadowRoot.querySelector(`[data-testid="${dataTestid}"]`),
        5000,
      )

      const candidates = [
        ...section.shadowRoot.querySelectorAll(`[data-testid="${dataTestid}"]`),
      ].filter((element) =>
        Boolean(element.querySelector(`[aria-checked="true"]`)),
      )

      if (candidates.length !== 1) {
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: `getRadioSelectedEl: Expected 1 candidate, found ${candidates.length}`,
        })
        return undefined
      }

      return candidates[0] as HTMLElement
    },
    {
      sections: sections.join(' > '),
      dataTestid,
    },
  )
}

export async function getRadioElByLabel(
  sections: string[],
  dataTestid: string,
  label: string,
) {
  return withSpan(
    'reddit.radioUtils.getRadioElByLabel',
    async (span) => {
      const section = await getNestedShadowrootSection(sections)

      const result = await getWaitForElementToAppearPromise<HTMLElement>(
        section,
        () =>
          section.shadowRoot.querySelector(
            `[data-testid="${dataTestid}"][aria-label="${label}"]`,
          ),
        5000,
      )

      if (!result) {
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: `getRadioElByLabel: Could not find element with label ${label}`,
        })
      }

      return result
    },
    {
      sections: sections.join(' > '),
      dataTestid,
      label,
    },
  )
}
